import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Order {
  id: string;
  customer_name: string;
  customer_email: string;
  amount: number;
  currency: string;
  usdt_amount: number;
  wallet_address: string;
  referral_code: string | null;
  fee: number;
  fee_percentage: string;
  total: number;
  payment_method: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: string;
  order_id: string;
  sender_type: 'user' | 'admin';
  message: string;
  created_at: string;
  is_read: boolean;
}

export default function AgentDashboardPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [agentName, setAgentName] = useState('');
  const [agentEmail, setAgentEmail] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'messages'>('details');
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const agentId = sessionStorage.getItem('agent_id');
    const name = sessionStorage.getItem('agent_name');
    const email = sessionStorage.getItem('agent_email');

    if (!agentId || !name || !email) {
      window.REACT_APP_NAVIGATE('/agent-login');
      return;
    }

    setAgentName(name);
    setAgentEmail(email);
    fetchOrders(name);
    fetchUnreadCounts(name);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('agent_id');
    sessionStorage.removeItem('agent_name');
    sessionStorage.removeItem('agent_email');
    window.REACT_APP_NAVIGATE('/agent-login');
  };

  const fetchOrders = async (referralCode: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('referral_code', referralCode)
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('Could not fetch orders:', error.message);
        setOrders([]);
        return;
      }
      setOrders(data || []);
    } catch (error) {
      console.warn('Could not fetch orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCounts = async (referralCode: string) => {
    try {
      // Get all order IDs for this agent
      const { data: agentOrders, error: ordersError } = await supabase
        .from('orders')
        .select('id')
        .eq('referral_code', referralCode);

      if (ordersError || !agentOrders) return;

      const orderIds = agentOrders.map(o => o.id);

      if (orderIds.length === 0) return;

      const { data, error } = await supabase
        .from('messages')
        .select('order_id')
        .in('order_id', orderIds)
        .eq('sender_type', 'user')
        .eq('is_read', false);

      if (error) {
        console.warn('Could not fetch unread counts:', error.message);
        return;
      }

      const counts: Record<string, number> = {};
      data?.forEach((msg) => {
        counts[msg.order_id] = (counts[msg.order_id] || 0) + 1;
      });

      setUnreadCounts(counts);
    } catch (error) {
      console.warn('Could not fetch unread counts:', error);
    }
  };

  const fetchOrderMessages = async (orderId: string) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);

      // Mark user messages as read
      const { error: updateError } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('order_id', orderId)
        .eq('sender_type', 'user')
        .eq('is_read', false);

      if (updateError) {
        console.warn('Error marking messages as read:', updateError);
      } else {
        await fetchUnreadCounts(agentName);
      }
    } catch (error) {
      console.warn('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedOrder) return;

    try {
      const { error } = await supabase
        .from('messages')
        .insert([
          {
            order_id: selectedOrder.id,
            sender_type: 'admin',
            message: newMessage.trim()
          }
        ]);

      if (error) throw error;
      
      setNewMessage('');
      await fetchOrderMessages(selectedOrder.id);
    } catch (error) {
      console.warn('Error sending message:', error);
      alert('メッセージの送信に失敗しました');
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', orderId);

      if (error) throw error;
      
      await fetchOrders(agentName);
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.warn('Error updating order status:', error);
      alert('ステータスの更新に失敗しました');
    }
  };

  const handleOrderClick = async (order: Order) => {
    setSelectedOrder(order);
    setActiveTab('details');
    await fetchOrderMessages(order.id);
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesSearch = 
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.wallet_address.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'deposited': return 'bg-blue-100 text-blue-800';
      case 'sent': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '保留中';
      case 'deposited': return '入金';
      case 'sent': return '送金';
      case 'cancelled': return 'キャンセル';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      timeZone: 'Asia/Bangkok'
    });
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const stats = {
    totalOrders: orders.length,
    pendingOrders: orders.filter(o => o.status === 'pending').length,
    depositedOrders: orders.filter(o => o.status === 'deposited').length,
    sentOrders: orders.filter(o => o.status === 'sent').length,
    cancelledOrders: orders.filter(o => o.status === 'cancelled').length,
    totalVolume: orders.reduce((sum, order) => sum + (order.usdt_amount || 0), 0),
    unreadMessages: Object.values(unreadCounts).reduce((sum, count) => sum + count, 0)
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">代理店管理画面</h1>
              <p className="mt-2 text-gray-600">紹介コード: <span className="font-semibold text-purple-600">{agentName}</span></p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">ログイン中</p>
                <p className="text-sm font-medium text-gray-900">{agentEmail}</p>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-logout-box-line text-lg"></i>
                <span>ログアウト</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-7 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">総注文数</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalOrders.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-shopping-cart-line text-2xl text-purple-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">保留中</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pendingOrders.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <i className="ri-time-line text-2xl text-yellow-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">入金</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.depositedOrders.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-bank-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">送金</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.sentOrders.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-send-plane-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">キャンセル</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats.cancelledOrders.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <i className="ri-close-line text-2xl text-red-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">総取引額</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{formatNumber(stats.totalVolume)}</p>
                <p className="text-xs text-gray-500 mt-1">USDT</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-2xl text-purple-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">未読メッセージ</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">{stats.unreadMessages.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="ri-message-3-line text-2xl text-orange-600"></i>
              </div>
            </div>
            {stats.unreadMessages > 0 && (
              <div className="absolute top-2 right-2">
                <span className="flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">検索</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="名前、メール、ウォレットアドレスで検索..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ステータス</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              >
                <option value="all">すべて</option>
                <option value="pending">保留中</option>
                <option value="deposited">入金</option>
                <option value="sent">送金</option>
                <option value="cancelled">キャンセル</option>
              </select>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">注文がありません</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">注文日時</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">お客様情報</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">金額</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">USDT</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">支払方法</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">ステータス</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">メッセージ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">操作</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => handleOrderClick(order)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(order.created_at).toLocaleString('ja-JP')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                        <div className="text-sm text-gray-500">{order.customer_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {Number(order.amount).toLocaleString()} {order.currency}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                        {formatNumber(Number(order.usdt_amount))} USDT
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order.payment_method}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {unreadCounts[order.id] > 0 ? (
                          <div className="flex items-center gap-2">
                            <span className="relative flex h-3 w-3">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                            </span>
                            <span className="text-sm font-semibold text-orange-600">
                              {unreadCounts[order.id]}件の新着
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOrderClick(order);
                          }}
                          className="text-purple-600 hover:text-purple-900 font-medium whitespace-nowrap"
                        >
                          詳細を見る
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">注文詳細</h2>
              <button
                onClick={() => setSelectedOrder(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              {/* Tabs */}
              <div className="flex space-x-4 mb-6">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'details'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  注文詳細
                </button>
                <button
                  onClick={() => setActiveTab('messages')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap relative ${
                    activeTab === 'messages'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  メッセージ ({messages.length})
                  {unreadCounts[selectedOrder.id] > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-5 w-5 bg-orange-500 items-center justify-center text-white text-xs font-bold">
                        {unreadCounts[selectedOrder.id]}
                      </span>
                    </span>
                  )}
                </button>
              </div>

              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="space-y-4">
                  {/* Status Update */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">ステータス更新</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'pending')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                          selectedOrder.status === 'pending'
                            ? 'bg-yellow-600 text-white'
                            : 'bg-white border border-yellow-600 text-yellow-600 hover:bg-yellow-50'
                        }`}
                      >
                        保留中
                      </button>
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'deposited')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                          selectedOrder.status === 'deposited'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-blue-600 text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        入金
                      </button>
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'sent')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                          selectedOrder.status === 'sent'
                            ? 'bg-green-600 text-white'
                            : 'bg-white border border-green-600 text-green-600 hover:bg-green-50'
                        }`}
                      >
                        送金
                      </button>
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                          selectedOrder.status === 'cancelled'
                            ? 'bg-red-600 text-white'
                            : 'bg-white border border-red-600 text-red-600 hover:bg-red-50'
                        }`}
                      >
                        キャンセル
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">注文ID</p>
                      <p className="text-sm font-medium text-gray-900 mt-1 break-all">{selectedOrder.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">注文日時</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {new Date(selectedOrder.created_at).toLocaleString('ja-JP')}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">お客様情報</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500">お名前</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">{selectedOrder.customer_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">メールアドレス</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">{selectedOrder.customer_email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">取引情報</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-500">金額</p>
                        <p className="text-sm font-medium text-gray-900">{Number(selectedOrder.amount).toLocaleString()} {selectedOrder.currency}</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-500">USDT換算</p>
                        <p className="text-sm font-medium text-gray-900">{formatNumber(Number(selectedOrder.usdt_amount))} USDT</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-500">ウォレットアドレス</p>
                        <p className="text-sm font-medium text-gray-900 font-mono break-all">{selectedOrder.wallet_address}</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-500">支払方法</p>
                        <p className="text-sm font-medium text-gray-900">{selectedOrder.payment_method}</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-sm text-gray-500">ステータス</p>
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedOrder.status)}`}>
                          {getStatusText(selectedOrder.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">顧客メッセージ</h3>
                  
                  <div className="max-h-64 overflow-y-auto mb-4 space-y-3">
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <i className="ri-chat-3-line text-4xl mb-2"></i>
                        <p>この注文にはメッセージがありません</p>
                      </div>
                    ) : (
                      messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-md px-4 py-3 rounded-lg relative ${
                            message.sender_type === 'admin'
                              ? 'bg-purple-600 text-white'
                              : message.is_read 
                                ? 'bg-white border border-gray-200 text-gray-900'
                                : 'bg-orange-50 border-2 border-orange-200 text-gray-900'
                          }`}>
                            {message.sender_type === 'user' && !message.is_read && (
                              <div className="absolute -top-2 -left-2">
                                <span className="flex h-4 w-4">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-4 w-4 bg-orange-500"></span>
                                </span>
                              </div>
                            )}
                            <p className="text-sm">{message.message}</p>
                            <p className={`text-xs mt-2 ${
                              message.sender_type === 'admin'
                                ? 'text-purple-100'
                                : message.is_read
                                  ? 'text-gray-500'
                                  : 'text-orange-600 font-semibold'
                            }`}>
                              {message.sender_type === 'admin' ? '代理店' : message.is_read ? '顧客' : '顧客（新着）'} • {formatDate(message.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="flex space-x-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      className="flex-1 bg-white border border-gray-300 text-gray-900 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="返信を入力..."
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-send-plane-fill"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
